import { Injectable } from '@angular/core';
import axios from 'axios';
import { ActionGroupId } from 'src/app/shared/payee-cases/types';
import { environment } from 'src/environments/environment';

export type LangData = {
  log_label: string;
  subject: string;
  title: string;
  message: string;
  is_containing_attachment: boolean;
};

export type InfoNotification = {
  id: number;
  name: string;
  transition_group: ActionGroupId;
  langs: Record<string, LangData>;
  created_at: string | null;
  process_states: string[];
};

export type CreateInfoNotificationRequest = {
  name: string;
  transition_group: ActionGroupId;
  langs: Record<string, LangData>;
  process_states: string[];
};

export type UpdateInfoNotificationRequest = {
  name: string;
  transition_group: ActionGroupId;
  langs: Record<string, LangData>;
  process_states: string[];
};

export type InfoNotificationsResponse = {
  data: InfoNotification[];
  pagination: {
    currentPage: number;
    perPage: number;
    total: number;
    lastPage: number;
  };
};

export type Process = {
  name: string;
  states: {
    class: string;
    label: string;
  }[];
};

@Injectable({
  providedIn: 'root'
})
export class InfoNotificationsService {
  async getInfoNotifications(
    page: number = 1,
    perPage: number = 10,
    name?: string,
    sortBy?: string,
    sortDirection: 'asc' | 'desc' = 'asc'
  ): Promise<InfoNotificationsResponse> {
    const result = await axios.get<InfoNotificationsResponse>(
      `${environment.baseUrl}/api/info-notification`,
      {
        params: {
          page,
          perPage,
          name,
          sortBy,
          sortDirection,
        },
      },
    );

    return result.data;
  }

  async createInfoNotification(request: CreateInfoNotificationRequest): Promise<InfoNotification> {
    const result = await axios.post<{
      info_notification: InfoNotification;
    }>(`${environment.baseUrl}/api/info-notification`, request);
    return result.data.info_notification;
  }

  async updateInfoNotification(id: number, request: UpdateInfoNotificationRequest): Promise<InfoNotification> {
    const result = await axios.put<{
      info_notification: InfoNotification;
    }>(`${environment.baseUrl}/api/info-notification/${id}`, request);
    return result.data.info_notification;
  }

  async deleteInfoNotification(id: number): Promise<void> {
    await axios.delete(`${environment.baseUrl}/api/info-notification/${id}`);
  }

  /**
   * Preview an info notification by sending an email to the current user
   */
  async previewInfoNotification(id: number, payee_case_reference_id: string, files: File[]): Promise<void> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files[]', file);
    }

    await axios.post(
      `${environment.baseUrl}/api/info-notification/preview/${id}/${payee_case_reference_id}`,
      formData,
    );
  }

  async getAllProcessStates(): Promise<Process[]> {
    const result = await axios.get<{
      processes: Process[];
    }>(`${environment.baseUrl}/api/workflow-process/states`);
    return result.data.processes;
  }
}
